import { defineComponent as _defineComponent } from 'vue'
import * as mux from '@mux/upchunk';
import { UpChunk } from '@mux/upchunk';
import type { AxiosResponse } from 'axios';
import type { DaturaImportJob, DaturaImportJobFile, DaturaImportJobRequest, RestErrorResponse } from 'farmdok-rest-api';
import zip from 'lodash.zip';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router/composables';

import ErrorUserMessage from '@/shared/components/ErrorUserMessage.vue';
import FileUploadTile from '@/shared/components/FileUploadTile.vue';
import SidebarHeadline from '@/shared/components/SidebarHeadline.vue';
import { createUuid } from '@/shared/modules/uuid';
import { useStore } from '@/store';


export default /*#__PURE__*/_defineComponent({
  __name: 'StepUpload',
  props: {
    assessmentName: { default: '' }
  },
  emits: ["upload:complete"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'upload:complete'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { assessmentName: string }

const store = useStore();
const router = useRouter();
const nextRoute = ref<() => void>(() => {});
const exceededMaximumUploadSize = ref(false);

router.beforeEach((to, from, next) => {
  if (from.name === 'drone/new' && !!totalProgress.value) {
    aboutToLeave.value = true;
    nextRoute.value = next;
    return false;
  }

  return next();
});





const aboutToLeave = ref(false);
const aboutToAbort = ref(false);
const processOrderId = computed<string>(() => store.getters['auth/currentProcessOrderIds'][0]);
const token = computed<string>(() => store.getters['auth/accessToken']());

const uploadedFiles = ref<File[]>([]);
const actionableFiles = ref<File[]>([]);
const failedFiles = ref<File[]>([]);
const uploadsInProgress = ref<mux.UpChunk[]>([]);
const filesWithUrls = ref<[File, string][]>([]);
const percentages = ref<Record<number, number>>({});
const totalProgress = computed<number | undefined>(() => {
  const values = Object.values<number>(percentages.value);
  if (!values.length) {
    return undefined;
  }

  return values.reduce((total, value) => total + value, 0);
});

const MAX_CONCURRENT_UPLOADS = 10;

async function upload(files: File[]): Promise<void> {
  percentages.value = { 0: 0 };
  actionableFiles.value = files;

  const request = toRequest(props.assessmentName, files);
  try {
    const response: DaturaImportJob = await store.dispatch('datura/import', request);
    const urls = response.files.map((file) => file.url);
    // definitely same length
    filesWithUrls.value = zip(files, urls).map(([file, url]) => [file!, url!]);

    filesWithUrls.value
      .filter((_, index) => index < MAX_CONCURRENT_UPLOADS)
      .map((_, index) => startUpload(index))
      .forEach(subscribeToUploadEvents);
  } catch (e) {
    determineError(e);
  }
}

function startUpload(index: number): UpChunk {
  const [file, endpoint] = filesWithUrls.value[index];
  const upChunk = mux.createUpload({
    file,
    endpoint,
    chunkSize: 1024 * 10,
    dynamicChunkSize: true,
    attempts: 200,
    delayBeforeAttempt: 10,
    headers: {
      Authorization: `Bearer ${token.value}`,
    },
    retryCodes: [408, 502, 503, 504, 400, 0],
  });
  uploadsInProgress.value.push(upChunk);
  return upChunk;
}

function subscribeToUploadEvents(upchunk: UpChunk, index: number): void {
  const [file] = filesWithUrls.value[index];
  const nextIndex = index + MAX_CONCURRENT_UPLOADS;

  upchunk.on('progress', (event) => {
    percentages.value[index] = event.detail;
    percentages.value = { ...percentages.value };
  });
  upchunk.on('success', () => {
    if (percentages.value[index] === 100) {
      uploadedFiles.value.push(file);
      if (nextIndex < filesWithUrls.value.length) {
        const nextUpchunk = startUpload(nextIndex);
        subscribeToUploadEvents(nextUpchunk, nextIndex);
      }
    }
  });
  upchunk.on('error', (errorEvent) => {
    if (errorEvent.detail.message === 'Server responded with 0. Stopping upload.') {
      return;
    }

    const body: { errorCode: string } = JSON.parse(errorEvent.detail.response.body);
    if (errorEvent.detail.response.statusCode === 400 && body.errorCode === 'concurrentModification') {
      return;
    }

    if (errorEvent.detail.response.statusCode === 400 && body.errorCode === 'invalidData') {
      upchunk.resume();
      return;
    }

    failedFiles.value.push(file);
    percentages.value = { ...percentages.value };
  });
}

function determineError({ response }: { response: AxiosResponse<RestErrorResponse> }): void {
  exceededMaximumUploadSize.value =
    response.status === 400 && response.data.errorUserMessage?.[0].includes('User has exceeded maximum upload size');
  failedFiles.value = actionableFiles.value;
}

function toRequest(assessmentName: string, files: File[]): DaturaImportJobRequest {
  return {
    id: createUuid(),
    name: assessmentName,
    files: files.map(
      (f): DaturaImportJobFile => ({
        id: createUuid(),
        filename: f.name,
        filesize: f.size,
      }),
    ),
    processOrderId: processOrderId.value,
  };
}

function abort(): void {
  uploadsInProgress.value.forEach((upchunk) => upchunk.abort());
  uploadsInProgress.value = [];
  percentages.value = {};
  aboutToAbort.value = false;
}

watch(totalProgress, (progress) => {
  if (progress === actionableFiles.value.length * 100 && failedFiles.value.length === 0) {
    percentages.value = {};
    emit('upload:complete');
  }
});

watch(token, (accessToken) => {
  uploadsInProgress.value.forEach((chunk) => {
    // eslint-disable-next-line no-param-reassign
    chunk.headers.Authorization = `Bearer ${accessToken}`;
  });
});

return { __sfc: true,store, router, nextRoute, exceededMaximumUploadSize, emit, props, aboutToLeave, aboutToAbort, processOrderId, token, uploadedFiles, actionableFiles, failedFiles, uploadsInProgress, filesWithUrls, percentages, totalProgress, MAX_CONCURRENT_UPLOADS, upload, startUpload, subscribeToUploadEvents, determineError, toRequest, abort, ErrorUserMessage, FileUploadTile, SidebarHeadline }
}

})